import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createApiCall } from 'services'

/**
 * The caller's own platform sessions, from the API's session registry (`user_sessions`).
 *
 * One row per full sign-in. `active` is computed server-side against the same bounds the
 * auth guards enforce on every request (7 days idle since last use, 30 days since the
 * sign-in), so a row shown as active is one that can actually still make requests.
 * No IP address and no raw user agent are stored or returned — only a coarse device label.
 */
export interface UserSession {
  id: string
  /** Coarse, e.g. "Chrome on macOS". Null when the browser sent no user agent. */
  deviceLabel: string | null
  /** The full sign-in (with second factor) that started this session. */
  createdAt: string
  /** Last silent renewal — roughly "last active", to within the 15-minute token lifetime. */
  lastSeenAt: string
  idleExpiresAt: string
  absoluteExpiresAt: string
  revokedAt: string | null
  revokedReason: string | null
  active: boolean
  /** The session making this request. Resolved server-side from the token. */
  current: boolean
}

const SESSIONS_QUERY_KEY = ['auth', 'sessions'] as const

export const useUserSessions = () => {
  const query = useQuery<{ sessions: UserSession[] }>({
    queryKey: SESSIONS_QUERY_KEY,
    queryFn: async () => {
      const response = await createApiCall({ method: 'GET', url: '/auth/admin/sessions' })
      if (response.status !== 200) {
        throw new Error('Could not load your devices')
      }
      return { sessions: (response.data?.sessions ?? []) as UserSession[] }
    },
  })
  return {
    data: query.data,
    isLoading: query.isLoading,
    refetch: query.refetch,
  }
}

export const useRevokeSession = () => {
  const queryClient = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: async (sessionId: string) => {
      const response = await createApiCall({
        method: 'DELETE',
        url: `/auth/admin/sessions/${sessionId}`,
      })
      if (response.status !== 200) {
        throw new Error('Could not sign out that device')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SESSIONS_QUERY_KEY })
    },
  })
}

export const useRevokeAllSessions = () => {
  const queryClient = useQueryClient()
  return useMutation<void, Error, void>({
    mutationFn: async () => {
      // No status check: `createApiCall` already REJECTS on >= 400, and this POST answers
      // 201, so the old `!== 200` check reported failure on every success.
      await createApiCall({
        method: 'POST',
        url: '/auth/admin/sessions/revoke-all',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SESSIONS_QUERY_KEY })
    },
  })
}

/**
 * Staff (platform_admin only — enforced by the API): sign ANOTHER user out of every
 * session. Audited server-side with the acting admin as `actor_user_id`.
 */
export const useRevokeUserSessions = () =>
  useMutation<{ revoked: number }, Error, string>({
    mutationFn: async (userId: string) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/auth/admin/users/${userId}/sessions/revoke-all`,
      })
      return { revoked: Number(response.data?.revoked ?? 0) }
    },
  })
